import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useStaffText } from './StaffText'

/**
 * A select the product draws itself.
 *
 * The native `<select>` is the one control a browser refuses to let an
 * application style. Its list is drawn by the operating system: macOS renders a
 * translucent panel in the system font, Windows a grey 1990s box, Android a
 * full-screen dialog. Whatever the rest of the screen looks like, the moment
 * somebody opens a dropdown they are looking at their operating system instead of
 * at this product — and on a kiosk or a hall board, at an OS chrome the room was
 * never meant to see.
 *
 * So this is a button and a list, with the keyboard contract the native control
 * has and the appearance the rest of the product has.
 *
 * What it keeps from the native control, because these are the things people
 * actually rely on and custom selects usually drop:
 *
 *   - Space, Enter, ArrowUp and ArrowDown open it.
 *   - Arrows move the highlight without committing, Enter commits, Escape
 *     abandons and puts the highlight back where it started.
 *   - Home and End jump to the ends.
 *   - Typing letters jumps to the first option starting with them, and typing
 *     more letters within a second refines rather than restarts — so "ba" finds
 *     Baku rather than landing on anything beginning with "a".
 *   - Focus returns to the trigger on close, always. A dropdown that drops focus
 *     on the body leaves a keyboard user at the top of the document.
 *
 * The list is portalled to the body and positioned from the trigger's measured
 * rectangle. Three bugs fall out of that one decision rather than being fixed
 * three times: a select inside a card with hidden overflow is not clipped, a
 * select inside a transformed ancestor is not dragged out of place by it, and a
 * select inside a `<label>` does not reopen the moment an option is clicked —
 * which it did, because a button is a labelable element, so the click bubbling to
 * the enclosing label activated the trigger again.
 */

export interface SelectOption {
  value: string
  label: string
  /** Shown to the right, quietly: a code, a count, a hint. */
  note?: string
  /** A shorter name for the closed control where room is tight (the top bar on a phone). */
  short?: string
  disabled?: boolean
  /** Draws the option in this face (the Designs font choice shows each font in itself). */
  fontFamily?: string
}

export interface SelectProps {
  value: string
  onChange: (value: string) => void
  /** The options, or `<option>` children.
   *
   *  Children are accepted because this replaced twenty-two native selects
   *  written over several months, and converting each one by hand is twenty-two
   *  chances to drop an option or invert a condition. Passing the existing
   *  `<option>` markup through unchanged made each conversion a rename. */
  options?: SelectOption[]
  children?: ReactNode
  /** Shown when nothing is chosen, and only then. Never used as the label. */
  placeholder?: string
  disabled?: boolean
  /** Matches the input primitive's small size. */
  size?: 'md' | 'sm'
  id?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  className?: string
  /** Laid out where a native select was, so the trigger keeps its width. */
  style?: React.CSSProperties
  /** Carried through from the controls this replaced. `name` and `required` do
   *  nothing here — there is no form submission behind them — but keeping them
   *  accepted meant the conversion did not have to decide, one call site at a
   *  time, whether removing them changed anything. */
  name?: string
  required?: boolean
  title?: string
  /** A class on the open list, for a list that needs its own width. */
  listClassName?: string
}

const TYPEAHEAD_RESET_MS = 1000

/** The list hangs from an ancestor marked data-select-anchor (a captioned pill), else the trigger. */
const anchorOf = (el: HTMLElement | null) => el?.closest<HTMLElement>('[data-select-anchor]') ?? el

export function Select({
  value,
  onChange,
  options: given,
  children,
  placeholder,
  disabled,
  size = 'md',
  id,
  className,
  style,
  title,
  listClassName,
  name: _name,
  required: _required,
  ...aria
}: SelectProps) {
  const { say } = useStaffText()
  const shown = placeholder ?? say('common.choose')
  const options = useMemo(() => given ?? optionsFromChildren(children), [given, children])

  const generatedId = useId()
  const triggerId = id ?? generatedId
  const listId = `${triggerId}-list`

  const trigger = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const typed = useRef({ text: '', at: 0 })

  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [box, setBox] = useState<{ top: number; left: number; width: number; drop: 'down' | 'up' } | null>(null)

  const selectedIndex = options.findIndex((o) => o.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined

  const place = useCallback(() => {
    const el = anchorOf(trigger.current)
    if (!el) return
    const r = el.getBoundingClientRect()
    // Opens upwards when there is not room below. A list that opens off the
    // bottom of the screen is a list whose last option cannot be reached.
    const below = window.innerHeight - r.bottom
    const drop: 'down' | 'up' = below < 240 && r.top > below ? 'up' : 'down'
    setBox({
      top: drop === 'down' ? r.bottom + 6 : r.top - 6,
      left: r.left,
      width: r.width,
      drop,
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    place()
    // Recomputed rather than followed: a scroll that moves the trigger should
    // move the list with it, and the cheapest correct answer is to measure again.
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, place])

  // A list wider than the room to its right is pulled left, toward the trigger's right edge.
  useLayoutEffect(() => {
    const el = list.current
    if (!open || !box || !el) return
    const room = window.innerWidth - 8
    if (box.left + el.offsetWidth <= room) return
    const right = anchorOf(trigger.current)?.getBoundingClientRect().right ?? room
    const left = Math.max(8, Math.min(right, room) - el.offsetWidth)
    if (left !== box.left) setBox({ ...box, left })
  }, [open, box])

  // Keep the highlighted row in view when the keyboard moves it.
  useEffect(() => {
    if (!open || active < 0) return
    list.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  const close = useCallback(
    (returnFocus = true) => {
      setOpen(false)
      setActive(-1)
      if (returnFocus) trigger.current?.focus()
    },
    [],
  )

  const commit = useCallback(
    (index: number) => {
      const option = options[index]
      if (!option || option.disabled) return
      onChange(option.value)
      close()
    },
    [close, onChange, options],
  )

  const step = useCallback(
    (from: number, delta: number) => {
      if (options.length === 0) return -1
      let i = from
      for (let n = 0; n < options.length; n++) {
        i = i + delta
        if (i < 0) i = 0
        if (i > options.length - 1) i = options.length - 1
        if (!options[i]?.disabled) return i
        // Walked into a run of disabled options at an end: stay put rather than
        // wrapping, which is what the native control does.
        if ((i === 0 && delta < 0) || (i === options.length - 1 && delta > 0)) return from
      }
      return from
    },
    [options],
  )

  const jumpToTyped = useCallback(
    (key: string) => {
      const now = Date.now()
      const text = now - typed.current.at < TYPEAHEAD_RESET_MS ? typed.current.text + key : key
      typed.current = { text, at: now }

      const found = options.findIndex(
        (o) => !o.disabled && o.label.toLowerCase().startsWith(text.toLowerCase()),
      )
      if (found < 0) return
      if (open) setActive(found)
      else commit(found)
    },
    [commit, open, options],
  )

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return

    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault()
        setOpen(true)
        setActive(selectedIndex >= 0 ? selectedIndex : step(-1, 1))
        return
      }
      if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault()
        jumpToTyped(event.key)
      }
      return
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        close()
        break
      case 'Tab':
        // Tab commits nothing and closes: the native control's behaviour, and the
        // one that does not surprise somebody filling a form quickly.
        close(false)
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        commit(active)
        break
      case 'ArrowDown':
        event.preventDefault()
        setActive((i) => step(i, 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        setActive((i) => step(i, -1))
        break
      case 'Home':
        event.preventDefault()
        setActive(step(-1, 1))
        break
      case 'End':
        event.preventDefault()
        setActive(step(options.length, -1))
        break
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault()
          jumpToTyped(event.key)
        }
    }
  }

  // A click anywhere else closes it. Pointerdown rather than click so that
  // pressing a button elsewhere on the page does not need two presses.
  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      const target = event.target as Node
      if (trigger.current?.contains(target) || list.current?.contains(target)) return
      close(false)
    }
    document.addEventListener('pointerdown', away, true)
    return () => document.removeEventListener('pointerdown', away, true)
  }, [close, open])

  return (
    <>
      <button
        ref={trigger}
        id={triggerId}
        type="button"
        className={['select-trigger', size === 'sm' ? 'select-trigger--sm' : '', className ?? '']
          .filter(Boolean)
          .join(' ')}
        disabled={disabled}
        style={style}
        title={title}
        onClick={() => {
          if (open) return close()
          setOpen(true)
          setActive(selectedIndex >= 0 ? selectedIndex : step(-1, 1))
        }}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        {...aria}
      >
        <span className="select-trigger__value" data-placeholder={selected ? undefined : 'true'} style={selected?.fontFamily ? { fontFamily: selected.fontFamily } : undefined}>
          {selected?.short ? (
            <>
              <span className="select-trigger__long">{selected.label}</span>
              <span className="select-trigger__short">{selected.short}</span>
            </>
          ) : (
            (selected?.label ?? shown)
          )}
        </span>
        <svg className="select-trigger__chevron" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open &&
        box &&
        createPortal(
          <div
              ref={list}
            id={listId}
            role="listbox"
            aria-labelledby={triggerId}
            tabIndex={-1}
            className={listClassName ? `select-list ${listClassName}` : 'select-list'}
            data-drop={box.drop}
            style={{ top: box.top, left: box.left, minWidth: box.width }}
            onKeyDown={onKeyDown}
          >
            {options.length === 0 && <div className="select-list__empty">{say('common.nothingToChoose')}</div>}
            {options.map((option, index) => (
              <div
                key={option.value}
                data-index={index}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined}
                className="select-option"
                data-active={index === active || undefined}
                data-chosen={option.value === value || undefined}
                data-disabled={option.disabled || undefined}
                onPointerEnter={() => !option.disabled && setActive(index)}
                onClick={() => commit(index)}
              >
                <svg className="select-option__tick" viewBox="0 0 12 12" aria-hidden="true">
                  <path d="M2.5 6.5 5 9l4.5-5.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="select-option__label" title={option.label} style={option.fontFamily ? { fontFamily: option.fontFamily } : undefined}>
                  {option.label}
                </span>
                {option.note && <span className="select-option__note">{option.note}</span>}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}

/**
 * Read `<option>` elements as options.
 *
 * Only what a native select supports: value, label, disabled. An `<optgroup>` is
 * flattened to its children — the product has none today, and a group that
 * silently vanished would be worse than one that renders flat.
 */
function optionsFromChildren(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = []
  const walk = (nodes: ReactNode) => {
    Children.forEach(nodes, (node) => {
      if (!isValidElement(node)) return
      const props = node.props as { value?: string | number; children?: ReactNode; disabled?: boolean }
      if (node.type === 'optgroup') {
        walk(props.children)
        return
      }
      if (node.type !== 'option') return
      out.push({
        value: String(props.value ?? ''),
        label: textOf(props.children),
        disabled: props.disabled,
      })
    })
  }
  walk(children)
  return out
}

function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOf).join('')
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children)
  return ''
}
